import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { api, apiPublic } from '@/plugins/axios.js'
import { vue } from '@/main.js'

/**
 * Retorna os dados por equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getDadosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await api.get(
      `/app/hpv-quadrivalente/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os dados por microárea
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 * @param {string} microarea
 *
 * @returns {Array}
 */
export async function getDadosPorMicroarea(cod_ibge, nu_ine, microarea) {
  try {
    const { data } = await api.get(
      `/app/hpv-quadrivalente/${cod_ibge}/equipe/${nu_ine}/microarea/${microarea}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Formata o array de dados com as doses
 *
 * @param {*} cadastros
 * @returns {Array}
 */
export function formatarCadastrosComDoses(cadastros) {
  return cadastros.map((item, index) => {
    const vacinasPendentes = JSON.parse(item.vacinas_pendentes)
    const vacinas = {
      hpv: JSON.parse(item.hpv) ?? [],
    }

    const vacinaConfig = [
      {
        nome: 'coresHpv',
        vacina: 'hpv',
        id_mn: '67',
        doses: [{ label: 'ÚNICA', id_ds: '9', idade: '9 a 14 anos' }],
      },
    ]

    const resultadoVacinas = vacinaConfig.reduce((acc, config) => {
      acc[config.nome] = processarDoses(
        vacinas[config.vacina],
        config.id_mn,
        config.doses,
        vacinasPendentes
      )
      return acc
    }, {})

    return {
      id: index,
      ...item,
      ...resultadoVacinas,
    }
  })
}

/**
 * Processa as doses configuradas da hpv
 * Exibe apenas uma dose, mas busca com prioridade:
 * ÚNICA (9) -> 2ª dose (2) -> 1ª dose (1)
 *
 * @param {Array} vacina Dados da vacina
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {Array} dosesConfig Configuração das doses para exibição
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @returns {Array} Lista de doses processadas
 */
function processarDoses(vacina, id_mn, dosesConfig, vacinasPendentes) {
  const configExibicao = dosesConfig[0]

  if (!configExibicao) {
    return []
  }

  const prioridades = ['9', '2', '1']

  let doseEncontrada = null

  for (const id_ds of prioridades) {
    const resultado = encontrarDose(vacina, id_mn, id_ds, vacinasPendentes)

    if (resultado.cor !== 'white') {
      doseEncontrada = resultado
      break
    }

    // guarda a última branca caso nenhuma seja encontrada
    if (!doseEncontrada) {
      doseEncontrada = resultado
    }
  }

  return [
    {
      dose: configExibicao.label,
      cor: doseEncontrada.cor,
      tag: doseEncontrada.tag || '',
      id_ds: configExibicao.id_ds || '',
      id_mn: id_mn || '',
      dt_plc:
        doseEncontrada.tipo === 'aplicada'
          ? doseEncontrada.dt_plc
          : configExibicao.idade,
      dt_plc_br:
        doseEncontrada.tipo === 'aplicada'
          ? doseEncontrada.dt_plc_br
          : configExibicao.idade,
      orig: doseEncontrada.orig || '',
      lt: doseEncontrada.lt || '',
    },
  ]
}

/**
 * Localiza a dose aplicada ou pendente de uma vacina.
 * @param {Array} doses Lista de doses aplicadas
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {String|Number} id_ds Identificador da dose
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @returns {Object} Dados da dose encontrada ou padrão
 */
function encontrarDose(doses, id_mn, id_ds, vacinasPendentes) {
  const aplicada = doses.find((el) => el.id_ds === id_ds && el.id_mn === id_mn)
  if (aplicada) {
    return {
      tipo: 'aplicada',
      cor: 'green',
      ...aplicada,
      dt_plc_br: aplicada.dt_plc?.split('-').reverse().join('/') || '',
    }
  }

  const pendente = vacinasPendentes?.find(
    (el) => el.id_ds === id_ds && el.id_mn === id_mn && el.na == '0'
  )
  if (pendente) {
    return {
      tipo: 'pendente',
      cor: 'red',
      ...pendente,
      dt_plc_br: '',
      orig: pendente.orig || '',
      lt: pendente.lt || '',
    }
  }

  return {
    tipo: 'pendente',
    id_ds: id_ds,
    id_mn: id_mn,
    cor: 'white',
    orig: '',
    lt: '',
  }
}

/**
 * Filtra os registros classificados como vermelhos.
 * @param {Array} cadastros Lista de registros
 * @returns {Array}
 */
export function exibirVermelhos(cadastros) {
  const cadastrosFormatados = formatarCadastrosComDoses(cadastros)

  const camposCores = ['coresHpv']

  return cadastrosFormatados.filter((item) =>
    camposCores.some(
      (campo) =>
        Array.isArray(item[campo]) &&
        item[campo].some((dose) => dose.cor === 'red')
    )
  )
}

/**
 * Filtra os registros classificados como verdes.
 * @param {Array} cadastros Lista de registros
 * @returns {Array}
 */
export function exibirVerdes(cadastros) {
  const cadastrosFormatados = formatarCadastrosComDoses(cadastros)

  const camposCores = ['coresHpv']

  const coresPermitidas = new Set(['green', 'white'])

  return cadastrosFormatados.filter((item) =>
    camposCores.every((campo) => {
      const arr = item[campo]
      return (
        Array.isArray(arr) && arr.every((el) => coresPermitidas.has(el.cor))
      )
    })
  )
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 */
export function generatePdf(cadastros, selectEquipe, microArea) {
  let title_pdf = 'HPV Quadrivalente'
  let titlePdfSave = utils.methods.titlePdf(
    'HPV Quadrivalente',
    selectEquipe.no_equipe
  )

  let description_pdf = 'Situação vacinal de crianças com HPV Quadrivalente'
  let headers_pdf = ['NOME', 'NASCIMENTO', 'MÃE', 'MA', 'CADASTRADO POR', 'HPV']
  var data_pdf = []
  let doses = formatarCadastrosComDoses(cadastros)
  for (let i = 0; i < doses.length; i++) {
    let obj = {}
    obj.no_cidadao = doses[i].no_cidadao
    obj.dt_nascimento = utils.methods.formatDate(doses[i].dt_nascimento)
    obj.no_mae = doses[i].no_mae
    obj.ma = doses[i].micro_area
    obj.cadastrado_por = doses[i].no_profissional
    obj.hpv = ''

    let coresHpv = doses[i].coresHpv
    let validDates = coresHpv.filter(
      (obj) =>
        obj.dt_plc_br &&
        obj.dt_plc_br.trim() !== '' &&
        obj.dt_plc_br != '9 a 14 anos'
    )
    if (validDates.length === 0) {
      obj.hpv = ''
    } else {
      let maiorDose = validDates.reduce((maior, atual) => {
        let [dia, mes, ano] = atual.dt_plc_br.split('/')
        let dataAtual = new Date(ano, mes - 1, dia)
        return dataAtual > maior ? dataAtual : maior
      }, new Date(0))

      let formatarData = (data) => {
        let dia = String(data.getDate()).padStart(2, '0')
        let mes = String(data.getMonth() + 1).padStart(2, '0')
        let ano = data.getFullYear()
        return `${dia}/${mes}/${ano}`
      }
      obj.hpv = formatarData(maiorDose)
    }
    data_pdf.push(obj)
  }
  let doc = new jsPDF({ orientation: 'l', ...utils.methods.encryptionPdf() })
  // cria a tabela
  let table = utils.methods.createTablePdf(data_pdf)
  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf
      )
      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          {
            label: 'Equipe',
            value: selectEquipe.no_equipe,
          },
          {
            label: 'CNES',
            value: selectEquipe.nu_cnes,
          },
          {
            label: 'INE',
            value: selectEquipe.ine_esf_eap,
          },
          {
            label: 'Microárea',
            value: microArea,
          },
        ],
        verticalPosition
      )

      doc.autoTable({
        head: [headers_pdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 8,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 8,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: function (data) {
          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
          }
        },
      })

      utils.methods.footerPdfCentralizado(doc, doc.autoTable.previous.finalY)

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

export const dataFields = {
  Nome: 'no_cidadao',
  Nascimento: {
    field: 'dt_nascimento',
    callback: (value) => {
      return value !== null ? utils.methods.formatDate(value) : ''
    },
  },
  Mãe: 'no_mae',
  MA: 'micro_area',
  'Cadastrado por': 'no_profissional',
  HPV: {
    callback: (item) => {
      let coresHpv = item.coresHpv
      let validDates = coresHpv.filter(
        (obj) =>
          obj.dt_plc_br &&
          obj.dt_plc_br.trim() !== '' &&
          obj.dt_plc_br != '9 a 14 anos'
      )
      if (validDates.length === 0) {
        return ''
      } else {
        let maiorDose = validDates.reduce((maior, atual) => {
          let [dia, mes, ano] = atual.dt_plc_br.split('/')
          let dataAtual = new Date(ano, mes - 1, dia)
          return dataAtual > maior ? dataAtual : maior
        }, new Date(0))

        let formatarData = (data) => {
          let dia = String(data.getDate()).padStart(2, '0')
          let mes = String(data.getMonth() + 1).padStart(2, '0')
          let ano = data.getFullYear()
          return `${dia}/${mes}/${ano}`
        }
        return formatarData(maiorDose)
      }
    },
  },
}

export const filterIdadeOptions = [
  { label: '9 - 14 anos', value: '9 - 14 anos' },
  { label: '15 - 19 anos', value: '15 - 19 anos' },
]

export function handleFilterIdade(data, filter) {
  let filtrado = data.filter((item) => {
    if (filter === null) {
      return item
    }
    if (filter === '9 - 14 anos') {
      return Number(item.idade_anos) >= 9 && Number(item.idade_anos) <= 14
    } else if (filter === '15 - 19 anos') {
      return Number(item.idade_anos) >= 15 && Number(item.idade_anos) <= 19
    } else {
      return item
    }
  })
  return filtrado
}
